import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { TOAST_STYLE, TOAST_ERROR_ICON } from '../toast/AppToaster';
import * as Sentry from '@sentry/react';
import { useTapSDK } from '../../hooks/useTapSDK';

interface TapCardProps {
  onTokenSuccess: (token: string) => void;
  amount?: number;
  currency?: string;
  mountId?: string;
  hideSubmit?: boolean;
  customerDetails?: any;
}

interface TapErrorData {
  message?: string;
  errors?: Array<{ message: string; code: string }>;
  error?: { message: string };
}

interface TapSuccessData {
  id: string;
  card: {
      id: string;
      first_six: string;
      last_four: string;
      brand: string;
      [key: string]: any;
  };
  [key: string]: any;
}

export const TapCardV2 = ({ onTokenSuccess, amount = 1, currency = 'SAR', mountId = 'card-sdk-id', hideSubmit = false, customerDetails = null }: TapCardProps) => {
    const { isLoaded: isSDKLoaded, error: sdkError, publicKey, merchantId } = useTapSDK();
    const initialized = useRef(false);
    const forceSized = useRef(false);
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const [renderError, setRenderError] = useState<string | null>(null);
    const [isTimedOut, setIsTimedOut] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const readyPollStarted = useRef(false);
    const loadingTimeoutRef = useRef<any>(null);

    // التحقق من صحة المبلغ وتنسيقه
    const validatedAmount = Math.max(0.01, Number(amount) || 0.01);

    // دمج أخطاء الـ SDK وأخطاء الرندر
    const error = sdkError || renderError;

    const handleRetry = useCallback(() => {
        setIsIframeLoaded(false);
        setRenderError(null);
        setIsTimedOut(false);
        setRetryCount(prev => prev + 1);
        initialized.current = false;
        forceSized.current = false;
    }, []);

    useEffect(() => {
        // إذا لم يتم تحميل الـ SDK أو كان هناك خطأ، لا تفعل شيئًا
        if (!isSDKLoaded || sdkError) {
            if (sdkError) setIsIframeLoaded(true); 
            return;
        }

        // تنظيف التايم أوت السابق
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);

        // تعيين تايم أوت للتحميل الكلي (15 ثانية)
        loadingTimeoutRef.current = setTimeout(() => {
            if (!isIframeLoaded && !initialized.current) {
                setIsTimedOut(true);
                setRenderError("تأخر تحميل نموذج الدفع. يرجى التحقق من اتصالك بالإنترنت.");
                setIsIframeLoaded(true);
                Sentry.withScope((scope) => {
                    scope.setLevel('error');
                    scope.setTag('source', 'tap_iframe_load');
                    scope.setTag('gateway', 'tap');
                    scope.setExtra('mount_id', mountId);
                    scope.setExtra('retry_count', retryCount);
                    if (customerDetails?.email) {
                        scope.setUser({ email: customerDetails.email, username: customerDetails.name || '' });
                    }
                    Sentry.captureMessage('Tap Card iframe load timeout (15s)');
                });
            }
        }, 15000);

        setIsIframeLoaded(false);
        forceSized.current = false;
        readyPollStarted.current = false;
        initialized.current = false;
        setIsTimedOut(false);
        setRenderError(null);

        let unmountFn: (() => void) | null = null;
        let resizeObserver: ResizeObserver | null = null;
        let timeoutId: any = null;

        const initCard = () => {
            // console.log('Initializing Tap Card SDK V2 (Retry:', retryCount, ')...');
            
            // تنظيف أي محتوى سابق
            const mountEl = document.getElementById(mountId);
            if (mountEl) mountEl.innerHTML = '';

            initialized.current = true;
            
            if (!window.CardSDK) {
                 setRenderError("لم يتم تحميل مكتبة الدفع بشكل صحيح.");
                 Sentry.withScope((scope) => {
                     scope.setLevel('error');
                     scope.setTag('source', 'tap_iframe_load');
                     scope.setTag('gateway', 'tap');
                     scope.setExtra('retry_count', retryCount);
                     if (customerDetails?.email) {
                         scope.setUser({ email: customerDetails.email, username: customerDetails.name || '' });
                     }
                     Sentry.captureMessage('Tap Card SDK: window.CardSDK is undefined at render time');
                 });
                 return;
            }

            const { renderTapCard, Currencies, Direction, Edges, Locale } = window.CardSDK;

            try {
                const merchantConfig = { 
                    id: merchantId,
                    domain: 'teen-sa.com'
                };
                
                const result = renderTapCard(mountId, {
                    publicKey: publicKey,
                    merchant: merchantConfig,
                    transaction: {
                        amount: validatedAmount,
                        currency: Currencies[currency] || Currencies.SAR
                    },
                    customer: {
                        editable: true,
                        contact: {
                            email: customerDetails?.email || '',
                            phone: {
                                countryCode: '966',
                                number: customerDetails?.phone || ''
                            }
                        }
                    },
                    acceptance: {
                        supportedBrands: ['VISA', 'MASTERCARD', 'MADA', 'AMERICAN_EXPRESS', 'APPLE_PAY'],
                        supportedCards: "ALL"
                    },
                    fields: {
                        cardHolder: true
                    },
                    addons: {
                        displayPaymentBrands: true,
                        loader: true,
                        saveCard: false
                    },
                    interface: {
                        locale: Locale.AR,
                        theme: 'light',
                        edges: Edges.CURVED,
                        direction: Direction.RTL,
                    },
                    style: {
                        base: {
                            color: 'oklch(0.2077 0.0398 265.75)',
                            lineHeight: '24px',
                            fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                            fontSmoothing: 'antialiased',
                            fontSize: '15px',
                            '::placeholder': {
                                color: 'oklch(0.7107 0.0351 256.79)',
                            }
                        },
                        invalid: {
                            color: 'oklch(0.61 0.25 28.5)',
                            iconColor: 'oklch(0.61 0.25 28.5)'
                        }
                    },
                    onError: (data: TapErrorData | string) => {
                        let errorMsg = 'حدث خطأ في نموذج الدفع';
                        
                        if (typeof data === 'string') {
                            errorMsg = data;
                        } else if (data?.message) {
                             errorMsg = data.message;
                        } else if (data?.errors?.[0]) {
                             errorMsg = data.errors[0].message || data.errors[0].code;
                        } else if (data?.error?.message) {
                             errorMsg = data.error.message;
                        }

                        // ✅ إرسال خطأ الفريم إلى Sentry مع بيانات المستخدم
                        Sentry.withScope((scope) => {
                            scope.setLevel('error');
                            scope.setTag('source', 'tap_iframe_load');
                            scope.setTag('gateway', 'tap');
                            scope.setExtra('raw_error', data);
                            scope.setExtra('retry_count', retryCount);
                            if (customerDetails?.email) {
                                scope.setUser({ email: customerDetails.email, username: customerDetails.name || '' });
                            }
                            Sentry.captureMessage(`Tap Card iframe onError: ${errorMsg}`);
                        });
                        
                        toast.error(errorMsg, {
                          id: 'tap-error',
                          style: TOAST_STYLE,
                          iconTheme: TOAST_ERROR_ICON,
                        });
                        setRenderError(`❌ ${errorMsg}`);
                        
                        setIsIframeLoaded(true);
                        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                    },
                    onSuccess: (data: TapSuccessData) => {
                        // console.log('Tap SDK Success:', data);
                        if (onTokenSuccess && data.id) {
                            onTokenSuccess(data.id);
                        }
                    }
                });

                if (result && result.unmount) {
                    unmountFn = result.unmount;
                }
            } catch (error: any) {
                const errorMsg = `❌ خطأ في تهيئة Tap SDK: ${error.message || error}`;
                setRenderError(errorMsg);
                setIsIframeLoaded(true);
                initialized.current = false;
                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                // ✅ إرسال استثناء تهيئة الفريم إلى Sentry مع بيانات المستخدم
                Sentry.withScope((scope) => {
                    scope.setLevel('error');
                    scope.setTag('source', 'tap_iframe_load');
                    scope.setTag('gateway', 'tap');
                    scope.setExtra('retry_count', retryCount);
                    scope.setExtra('mount_id', mountId);
                    if (customerDetails?.email) {
                        scope.setUser({ email: customerDetails.email, username: customerDetails.name || '' });
                    }
                    Sentry.captureException(error);
                });
                return;
            }

            // Force Iframe Width Logic
            const forceIframeWidth = () => {
                if (forceSized.current) return;
                const mountEl = document.getElementById(mountId);
                const iframe = mountEl?.querySelector('iframe');
                if (!mountEl || !iframe) {
                    setTimeout(forceIframeWidth, 50);
                    return;
                }
                try {
                    const updateWidth = () => {
                         const w = mountEl.getBoundingClientRect().width;
                         iframe.style.setProperty('width', `${Math.floor(w)}px`, 'important');
                         iframe.style.setProperty('max-width', `${Math.floor(w)}px`, 'important');
                         iframe.style.setProperty('min-width', `${Math.floor(w)}px`, 'important');
                         iframe.style.setProperty('display', 'block', 'important');
                    };
                    updateWidth();
                    forceSized.current = true;

                    // Wait for UI Ready Logic
                    // Note: We can't access contentDocument due to cross-origin restrictions
                    // So we rely only on the load event which is safe
                    const waitUntilTapUiReady = () => {
                        if (readyPollStarted.current) return;
                        readyPollStarted.current = true;
                        
                        // Set a timeout as fallback
                        const timeoutId = setTimeout(() => {
                            if (!isIframeLoaded) {
                                setIsIframeLoaded(true);
                                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                            }
                        }, 3000); // 3 seconds timeout

                        // Listen to load event (safe for cross-origin iframes)
                        iframe.addEventListener('load', () => {
                            clearTimeout(timeoutId);
                            requestAnimationFrame(() => {
                                setIsIframeLoaded(true);
                                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                            });
                        }, { once: true });
                        
                        // Check if iframe is already loaded (check src attribute)
                        try {
                            if (iframe.src && iframe.src !== 'about:blank') {
                                // Iframe has a source, wait a bit then check
                                setTimeout(() => {
                                    clearTimeout(timeoutId);
                                    requestAnimationFrame(() => {
                                        setIsIframeLoaded(true);
                                        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                                    });
                                }, 500);
                            }
                        } catch (e) {
                            // Ignore cross-origin errors
                        }
                    };

                    // Note: We cannot access contentDocument due to cross-origin restrictions
                    // This would cause "Blocked frame" errors in console
                    // We rely only on the load event and complete property which are safe
                    iframe.addEventListener('load', waitUntilTapUiReady, { once: true });

                    resizeObserver = new ResizeObserver(updateWidth);
                    resizeObserver.observe(mountEl);
                } catch (e) { }
            };
            forceIframeWidth();
        };

        // تأخير بسيط للتأكد من جاهزية الـ DOM
        timeoutId = setTimeout(initCard, 100);

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
            initialized.current = false;
            if (unmountFn) unmountFn();
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [amount, currency, onTokenSuccess, mountId, customerDetails, isSDKLoaded, sdkError, publicKey, merchantId, retryCount]);

    const handleTokenize = () => {
        if (window.CardSDK) window.CardSDK.tokenize();
    };

    return (
        <div className="tap-card-wrapper">
            <div className={`tap-sdk-mount ${isIframeLoaded ? 'is-ready' : 'is-loading'}`}>
                {!isIframeLoaded && !error && (
                    <div className="tap-sdk-loading" role="status" aria-live="polite" aria-label="جاري تحميل نموذج الدفع">
                        <div className="tap-spinner" aria-hidden="true"></div>
                        <div className="tap-loading-text">جاري تحميل نموذج الدفع…</div>
                    </div>
                )}
                {error && (
                    <div className="tap-sdk-error-message" style={{ textAlign: 'center', padding: '20px' }}>
                        <p style={{ color: 'oklch(0.61 0.25 28.5)', marginBottom: '10px' }}>{error}</p>
                        <button 
                            onClick={handleRetry}
                            style={{
                                backgroundColor: 'oklch(0.9683 0.0069 247.9)',
                                border: '1px solid oklch(0.869 0.0198 252.89)',
                                padding: '5px 15px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            إعادة المحاولة
                        </button>
                    </div>
                )}
                <div id={mountId}></div>
            </div>
            {!hideSubmit && (
                <button id="card-v2" className="submit-button" onClick={handleTokenize}>
                    إتمام الدفع
                </button>
            )}
        </div>
    );
};
